// src/components/GameCard.js
import React from "react";
import { Link } from "react-router-dom";
import { getAbbreviation } from "../utils/abbreviations";
import "./GameCard.css";

// ── Bookmaker icon map ────────────────────────────────────────────────────────
const BOOKMAKER_ICONS = {
  "DraftKings":           "/images/draftkings_icon.png",
  "FanDuel":              "/images/fanduel_icon.png",
  "BetMGM":               "/images/bet_mgm_icon.png",
  "Caesars":              "/images/caesars_icon.png",
  "Hard Rock Bet":        "/images/hard_rock_bet_icon.png",
  "Hard Rock Bet (AZ)":   "/images/hard_rock_bet_icon.png",
  "Hard Rock Bet (FL)":   "/images/hard_rock_bet_icon.png",
  "Hard Rock Bet (OH)":   "/images/hard_rock_bet_icon.png",
  "ESPN BET":             "/images/espn_bet_icon.png",
  "theScore Bet":         "/images/espn_bet_icon.png",
  "BetRivers":            "/images/betrivers_icon.png",
  "Fanatics":             "/images/fanatics_icon.png",
  "Bally Bet":            "/images/bally_bet_icon.png",
  "BetParx":              "/images/betparx.jpeg",
  "BetUS":                "/images/betus.jpeg",
  "Bovada":               "/images/bovada.webp",
  "LowVig.ag":            "/images/lowvig.jpg",
  "Novig":                "/images/novig.png",
  "ReBet":                "/images/rebet.jpg",
  "Betr DFS":             "/images/betr.jpg",
  "DraftKings Pick6":     "/images/pick6.png",
  "PrizePicks":           "/images/prizepicks.jpg",
  "Underdog":             "/images/underdog.png",
  "Kalshi":               "/images/kalshi.png",
  "Polymarket":           "/images/polymarket.png",
  "ProphetX":             "/images/prophetx.png",
};

function getBookmakerIcon(name) {
  if (!name) return null;
  return BOOKMAKER_ICONS[name] || null;
}

// ── Format helpers ────────────────────────────────────────────────────────────

function fmtOdds(val) {
  const n = parseFloat(val);
  if (!Number.isFinite(n)) return "–";
  return n > 0 ? `+${n}` : `${n}`;
}

function fmtEv(val) {
  const n = Number(val);
  if (!Number.isFinite(n)) return null;
  const pct = (n * 100).toFixed(1);
  return n >= 0 ? `+${pct}%` : `${pct}%`;
}

function fmtSpread(pt) {
  const n = parseFloat(pt);
  if (!Number.isFinite(n)) return "";
  return n > 0 ? `+${n}` : `${n}`;
}

function compareMarketVariants(a, b) {
  const evA = Number(a?.expected_value);
  const evB = Number(b?.expected_value);
  const safeEvA = Number.isFinite(evA) ? evA : Number.NEGATIVE_INFINITY;
  const safeEvB = Number.isFinite(evB) ? evB : Number.NEGATIVE_INFINITY;
  if (safeEvA !== safeEvB) return safeEvB - safeEvA;

  const priceA = Number(a?.price);
  const priceB = Number(b?.price);
  const safePriceA = Number.isFinite(priceA) ? priceA : Number.NEGATIVE_INFINITY;
  const safePriceB = Number.isFinite(priceB) ? priceB : Number.NEGATIVE_INFINITY;
  if (safePriceA !== safePriceB) return safePriceB - safePriceA;

  // Numeric comparison fixes ordering bugs like '9.5' vs '10.5'
  const plA = Number(a?.point_line ?? 0);
  const plB = Number(b?.point_line ?? 0);
  if (Number.isFinite(plA) && Number.isFinite(plB)) return plB - plA;
  return 0;
}

// ── EvLabel: colored EV display ───────────────────────────────────────────────
function EvLabel({ item }) {
  const ev = item ? fmtEv(item.expected_value) : null;
  if (!ev) return <p className="gc-ev gc-ev--null">–</p>;
  const rawEv = Number(item.expected_value);
  const evClass = rawEv >= 0 ? 'gc-ev--pos' : rawEv >= -0.01 ? 'gc-ev--slight-neg' : 'gc-ev--neg';
  return <p className={`gc-ev ${evClass}`}>{ev}</p>;
}

// ── BookIcon: renders icon img or nothing if unavailable ──────────────────────
function BookIcon({ bookmaker, side }) {
  const icon = getBookmakerIcon(bookmaker);
  if (!icon) return null;
  return (
    <img
      src={icon}
      alt={bookmaker}
      className={`gc-book-icon gc-book-icon--${side}`}
      loading="lazy"
      width={38}
      height={38}
    />
  );
}

// ── OutcomeCell: single outcome column for 3-way market rows ────────────────
// Renders a stacked bookmaker icon (if available) → label → odds → EV.
// Used exclusively by the 3-way (soccer/cricket) layout path.
function OutcomeCell({ item, label }) {
  const icon = item ? getBookmakerIcon(item.bookmaker) : null;
  return (
    <div className="gc-3way-cell">
      <div className="gc-3way-outcome-top">
        {icon && <img src={icon} alt={item.bookmaker} className="gc-3way-book-icon" loading="lazy" width={26} height={26} />}
        <p className="gc-label-top">{label}</p>
      </div>
      <p className="gc-odds">{item ? fmtOdds(item.price) : "-"}</p>
      {item ? <EvLabel item={item} /> : <p className="gc-ev gc-ev--null">-</p>}
    </div>
  );
}

// ── GameCard ──────────────────────────────────────────────────────────────────
/**
 * Main lines EV card (Moneyline / Spread / Total).
 *
 * Vertically stacked markets: left team | center labels | right team.
 * Header shows full team names (no abbreviations).
 * Market rows use abbreviations (ML), spread points (SPR), O/U (TOT).
 *
 * item.expected_value = state_ev (state-aware) or ev_best (global fallback).
 * item.price          = state_best_price or best_price.
 * item.bookmaker      = state_best_bookmaker or best_bookmaker.
 */
function GameCard({ analyzedData, markets, homeTeam, awayTeam, commenceTime, isBlurred = false }) {
  const items = Array.isArray(analyzedData) ? analyzedData : [];
  const groupedMarkets = markets && !Array.isArray(markets) ? markets : null;

  if (process.env.NODE_ENV !== 'production') {
    console.debug('[GameCard] Rendering:', {
      homeTeam,
      awayTeam,
      h2hRows:    groupedMarkets?.h2h?.length    ?? items.filter(i => i.market === 'h2h').length,
      spreadRows: groupedMarkets?.spreads?.length ?? items.filter(i => i.market === 'spreads').length,
      totalRows:  groupedMarkets?.totals?.length  ?? items.filter(i => i.market === 'totals').length,
      propsKeys:  groupedMarkets?.props ? Object.keys(groupedMarkets.props).length : 0,
    });
  }

  const awayAbbr = getAbbreviation(awayTeam);
  const homeAbbr = getAbbreviation(homeTeam);

  const cmp = s => (s ?? "").toLowerCase().trim();
  // Match the actual market outcome label. For main-line side markets the
  // outcome team is carried in `player` or `team`; `home_team` / `away_team`
  // describe the game and would incorrectly match both spread rows.
  // Fallback: when both player and team are empty (backend omits them), derive
  // the side from point_line sign — positive line = away-team row.
  const matchOutcomeTeam = (item, teamName) => {
    if (cmp(item.player) === cmp(teamName) || cmp(item.team) === cmp(teamName)) return true;
    // Fallback for rows where player/team are not populated
    if (!item.player && !item.team && item.market === 'spreads') {
      const pl = Number(item.point_line);
      if (Number.isFinite(pl)) {
        // Positive spread line → away-team row; negative → home-team row.
        // This heuristic fires only when the backend omits player/team fields.
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[GameCard] matchOutcomeTeam: falling back to sign-based spread matching — backend row is missing player/team fields', {
            point_line: pl, teamName, away_team: item.away_team, home_team: item.home_team,
          });
        }
        const isPositiveLine = pl > 0;
        if (isPositiveLine && cmp(item.away_team) === cmp(teamName)) return true;
        if (!isPositiveLine && cmp(item.home_team) === cmp(teamName)) return true;
      }
    }
    return false;
  };

  const selectBestMatch = predicate => {
    const matches = items.filter(predicate);
    if (matches.length === 0) return undefined;
    if (matches.length === 1) return matches[0];
    return [...matches].sort(compareMarketVariants)[0];
  };

  // bet_type comparison is case-insensitive and prefix-aware:
  // handles 'Over', 'over', 'OVER', and edge cases like 'Over 215.5'
  const cmpBetType = raw => String(raw ?? '').trim().toLowerCase();
  const isOver  = bt => cmpBetType(bt).startsWith('over')  || cmpBetType(bt) === 'o';
  const isUnder = bt => cmpBetType(bt).startsWith('under') || cmpBetType(bt) === 'u';

  let awayML;
  let homeML;
  let awaySpread;
  let homeSpread;
  let overTotal;
  let underTotal;
  let drawML;

  if (groupedMarkets) {
    const h2hRows = Array.isArray(groupedMarkets.h2h) ? groupedMarkets.h2h : [];
    const spreadRows = Array.isArray(groupedMarkets.spreads) ? groupedMarkets.spreads : [];
    const totalRows = Array.isArray(groupedMarkets.totals) ? groupedMarkets.totals : [];

    drawML = h2hRows.find(i => cmp(i?.player) === 'draw' || cmp(i?.team) === 'draw');
    const sideH2HRows = h2hRows.filter(item => item && item !== drawML);

    awayML = sideH2HRows.find(i => matchOutcomeTeam(i, awayTeam)) || sideH2HRows[0];
    homeML = sideH2HRows.find(i => i !== awayML && matchOutcomeTeam(i, homeTeam))
      || sideH2HRows.find(i => i !== awayML)
      || sideH2HRows[1];

    awaySpread = spreadRows.find(i => matchOutcomeTeam(i, awayTeam)) || spreadRows[0];
    homeSpread = spreadRows.find(i => i !== awaySpread && matchOutcomeTeam(i, homeTeam))
      || spreadRows.find(i => i !== awaySpread)
      || spreadRows[1];

    overTotal = totalRows.find(i => isOver(i?.bet_type)) || totalRows[0];
    underTotal = totalRows.find(i => i !== overTotal && isUnder(i?.bet_type))
      || totalRows.find(i => i !== overTotal)
      || totalRows[1];
  } else {
    awayML     = items.find(i => i.market === 'h2h' && matchOutcomeTeam(i, awayTeam));
    homeML     = items.find(i => i.market === 'h2h' && matchOutcomeTeam(i, homeTeam));
    awaySpread = selectBestMatch(i => i.market === 'spreads' && matchOutcomeTeam(i, awayTeam));
    homeSpread = selectBestMatch(i => i.market === 'spreads' && matchOutcomeTeam(i, homeTeam));
    overTotal  = selectBestMatch(i => i.market === 'totals' && isOver(i.bet_type));
    underTotal = selectBestMatch(i => i.market === 'totals' && isUnder(i.bet_type));

    // Draw outcome — present in soccer / cricket h2h markets.
    // Detected from data so no sport-specific prop is required.
    drawML = items.find(i => i.market === 'h2h' &&
      (cmp(i.player) === 'draw' || cmp(i.team) === 'draw'));
  }

  const is3Way  = Boolean(drawML);

  const gameTime = commenceTime
    ? new Date(commenceTime).toLocaleString("en-US", {
        month: "short", day: "numeric",
        hour: "numeric", minute: "2-digit", hour12: true,
        timeZone: "America/New_York",
      })
    : "";

  return (
    <div className={`gc-card${isBlurred ? " gc-card--locked" : ""}`}>

      {/* ── Header: full team names + time ──────────────────────────── */}
      <div className="gc-header">
        <div className="gc-time">{gameTime && `${gameTime} ET`}</div>
        <div className="gc-matchup">
          <span className="gc-team-name">{awayTeam}</span>
          <span className="gc-at">@</span>
          <span className="gc-team-name">{homeTeam}</span>
        </div>
      </div>

      {/* ── Market sections — vertically stacked ────────────────────── */}
      <div className={`gc-market-container${isBlurred ? " gc-market-container--blurred" : ""}`}>

        {is3Way ? (

          /* ── 3-way layout: soccer / cricket (Labels | Away | Home | Draw) ── */
          <>
            {(awayML || homeML || drawML) && (
              <div className="gc-market">
                <div className="gc-separator" />
                <div className="gc-market-row gc-market-row--3way">
                  <div className="gc-3way-cell gc-3way-cell--labels">
                    <span className="gc-market-title">Winner</span>
                    <span className="gc-center-lbl gc-center-lbl--odds">Best Odds</span>
                    <span className="gc-center-lbl gc-center-lbl--ev">Exp. Value</span>
                  </div>
                  <OutcomeCell item={awayML}  label={awayAbbr} />
                  <OutcomeCell item={homeML}  label={homeAbbr} />
                  <OutcomeCell item={drawML}  label="Draw" />
                </div>
              </div>
            )}

            {(awaySpread || homeSpread) && (
              <div className="gc-market">
                <div className="gc-separator" />
                <div className="gc-market-row gc-market-row--3way">
                  <div className="gc-3way-cell gc-3way-cell--labels">
                    <span className="gc-market-title">Spread</span>
                    <span className="gc-center-lbl gc-center-lbl--odds">Best Odds</span>
                    <span className="gc-center-lbl gc-center-lbl--ev">Exp. Value</span>
                  </div>
                  <OutcomeCell item={awaySpread} label={awaySpread ? fmtSpread(awaySpread.point_line) : "-"} />
                  <OutcomeCell item={homeSpread} label={homeSpread ? fmtSpread(homeSpread.point_line) : "-"} />
                  <OutcomeCell item={null} label="-" />
                </div>
              </div>
            )}

            {(overTotal || underTotal) && (
              <div className="gc-market">
                <div className="gc-separator" />
                <div className="gc-market-row gc-market-row--3way">
                  <div className="gc-3way-cell gc-3way-cell--labels">
                    <span className="gc-market-title">Total</span>
                    <span className="gc-center-lbl gc-center-lbl--odds">Best Odds</span>
                    <span className="gc-center-lbl gc-center-lbl--ev">Exp. Value</span>
                  </div>
                  <OutcomeCell item={overTotal}  label={overTotal  ? `O ${overTotal.point_line}`  : "-"} />
                  <OutcomeCell item={underTotal} label={underTotal ? `U ${underTotal.point_line}` : "-"} />
                  <OutcomeCell item={null} label="-" />
                </div>
              </div>
            )}
          </>

        ) : (

          /* ── Standard 2-way layout (unchanged) ──────────────────────────── */
          <>
            {/* Moneyline */}
            {(awayML || homeML) && (
              <div className="gc-market">
                <div className="gc-separator" />
                <div className="gc-market-row">

                  <div className="gc-side gc-side--left">
                    {awayML && (
                      <div className="gc-bet-info gc-bet-info--left">
                        <BookIcon bookmaker={awayML.bookmaker} side="left" />
                        <div className="gc-info-stack gc-info-stack--left">
                          <p className="gc-label-top">{awayAbbr}</p>
                          <p className="gc-odds">{fmtOdds(awayML.price)}</p>
                          <EvLabel item={awayML} />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="gc-center">
                    <span className="gc-market-title">Winner</span>
                    <span className="gc-center-lbl gc-center-lbl--odds">Best Odds</span>
                    <span className="gc-center-lbl gc-center-lbl--ev">Expected Value</span>
                  </div>

                  <div className="gc-side gc-side--right">
                    {homeML && (
                      <div className="gc-bet-info gc-bet-info--right">
                        <div className="gc-info-stack gc-info-stack--right">
                          <p className="gc-label-top">{homeAbbr}</p>
                          <p className="gc-odds">{fmtOdds(homeML.price)}</p>
                          <EvLabel item={homeML} />
                        </div>
                        <BookIcon bookmaker={homeML.bookmaker} side="right" />
                      </div>
                    )}
                  </div>

                </div>
              </div>
            )}

            {/* Spread */}
            {(awaySpread || homeSpread) && (
              <div className="gc-market">
                <div className="gc-separator" />
                <div className="gc-market-row">

                  <div className="gc-side gc-side--left">
                    {awaySpread && (
                      <div className="gc-bet-info gc-bet-info--left">
                        <BookIcon bookmaker={awaySpread.bookmaker} side="left" />
                        <div className="gc-info-stack gc-info-stack--left">
                          <p className="gc-label-top">{fmtSpread(awaySpread.point_line)}</p>
                          <p className="gc-odds">{fmtOdds(awaySpread.price)}</p>
                          <EvLabel item={awaySpread} />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="gc-center">
                    <span className="gc-market-title">Spread</span>
                    <span className="gc-center-lbl gc-center-lbl--odds">Best Odds</span>
                    <span className="gc-center-lbl gc-center-lbl--ev">Expected Value</span>
                  </div>

                  <div className="gc-side gc-side--right">
                    {homeSpread && (
                      <div className="gc-bet-info gc-bet-info--right">
                        <div className="gc-info-stack gc-info-stack--right">
                          <p className="gc-label-top">{fmtSpread(homeSpread.point_line)}</p>
                          <p className="gc-odds">{fmtOdds(homeSpread.price)}</p>
                          <EvLabel item={homeSpread} />
                        </div>
                        <BookIcon bookmaker={homeSpread.bookmaker} side="right" />
                      </div>
                    )}
                  </div>

                </div>
              </div>
            )}

            {/* Total */}
            {(overTotal || underTotal) && (
              <div className="gc-market">
                <div className="gc-separator" />
                <div className="gc-market-row">

                  <div className="gc-side gc-side--left">
                    {overTotal && (
                      <div className="gc-bet-info gc-bet-info--left">
                        <BookIcon bookmaker={overTotal.bookmaker} side="left" />
                        <div className="gc-info-stack gc-info-stack--left">
                          <p className="gc-label-top">O {overTotal.point_line}</p>
                          <p className="gc-odds">{fmtOdds(overTotal.price)}</p>
                          <EvLabel item={overTotal} />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="gc-center">
                    <span className="gc-market-title">Total</span>
                    <span className="gc-center-lbl gc-center-lbl--odds">Best Odds</span>
                    <span className="gc-center-lbl gc-center-lbl--ev">Expected Value</span>
                  </div>

                  <div className="gc-side gc-side--right">
                    {underTotal && (
                      <div className="gc-bet-info gc-bet-info--right">
                        <div className="gc-info-stack gc-info-stack--right">
                          <p className="gc-label-top">U {underTotal.point_line}</p>
                          <p className="gc-odds">{fmtOdds(underTotal.price)}</p>
                          <EvLabel item={underTotal} />
                        </div>
                        <BookIcon bookmaker={underTotal.bookmaker} side="right" />
                      </div>
                    )}
                  </div>

                </div>
              </div>
            )}
          </>

        )}

      </div>

      {/* ── Lock overlay (mirrors BestBetCard .bbc-lock-overlay) ──────── */}
      {isBlurred && (
        <div className="gc-lock-overlay">
          <img src="/images/unlock_icon.png" alt="Unlock" className="gc-lock-icon" />
          <span className="gc-lock-title">Unlock All Games</span>
          <p className="gc-lock-copy">Subscribe to unlock all betting opportunities.</p>
          <Link to="/subscribe" className="gc-lock-link">Subscribe Now</Link>
        </div>
      )}
    </div>
  );
}

// Memoized: prevents re-rendering every card in a list when unrelated sibling state changes.
export default React.memo(GameCard);
